import {
  Competency_Map_Status,
  CompetencyMapGenQuestionsLogQueryResult,
  useCompetencyMapGenQuestionsLogQuery,
  useCompetencyMapQuery,
} from "@generated/graphql";
import { Box, Button, Grid, Stack, Tooltip } from "@mui/material";
import ButtonWithPermissions from "components/buttons/ButtonWithPermissions";
import CircularLoading from "components/CircularLoading";
import Form from "components/form/Form";
import GenerateQuestionsForCMModal from "components/modals/GenerateQuestionsForCMModal";
import { Competency_Map_Tabs_Values } from "constants/global";
import { CompetencyMapSelectedParts } from "helpers/competencyMapSelectParts";
import { getTestitngTypeTitle, secondsToMinutes } from "helpers/index";
import useIsCreate from "hooks/useIsCreate";
import useSetTabTitle from "hooks/useSetTabTitle";
import TabLayout, { TabLayoutProps } from "layout/TabLayout";
import { getCompetencyMapWithProgressInfo } from "pages/CompetencyMaps/view/components/ProgressInfo/utils";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import ActionButtons from "./components/ActionButtons";
import CopyMapModal from "./components/CopyMapModal";
import Header from "./components/Header";
import MoreActionsButton from "./components/MoreActionsButton";
import ProgressInfo from "./components/ProgressInfo";
import { CompetencyMapWithProgressInfo } from "./components/ProgressInfo/types";
import SelectionActions from "./components/Selection/SelectionActions";
import { SelectedEntityIds, SelectionEntity, SelectionScope } from "./components/Selection/types";
import {
  buildSelectionTree,
  EMPTY_SELECTED_ENTITY_IDS,
  normalizeSelectedEntityIds,
  toggleSelectionEntity,
} from "./components/Selection/utils";
import { CompetencyMapInputs } from "./components/types";
import { ConstructorTabContext } from "./context";
import ConstructorTab from "./tabs/ConstructorTab";
import DocumentsTab from "./tabs/DocumentsTab";
import QuestionsListTab from "./tabs/QuestionsListTab";
import TestingListTab from "./tabs/TestingListTab";

type CopyModalState = {
  selectParts?: CompetencyMapSelectedParts;
  titleText?: string;
  inputLabel?: string;
  actionButtonText?: string;
};

const structureSelectionModeTooltipText = [
  "Выберите компетенции, блоки знаний или навыки, чтобы удалить их,",
  "сгенерировать вопросы или запустить тестирование только по выбранным объектам.",
].join(" ");

const questionsSelectionModeTooltipText =
  "Выберите вопросы, чтобы подтвердить, отклонить, запустить тестирование или удалить.";

const CompetencyMapView = () => {
  const [generateQuestionsForCMModalData, setGenerateQuestionsForCMModalData] = useState<{
    selectParts?: CompetencyMapSelectedParts;
    hideScopeAndPrompts?: boolean;
  } | null>(null);
  const isCreate = useIsCreate();
  const [isEditable, setIsEditable] = useState<boolean>(isCreate);
  const [copyModalState, setCopyModalState] = useState<CopyModalState | null>(null);
  const [navigationOpen, setNavigationOpen] = useState(true);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);
  const [skillsIdsToDelete, setSkillsIdsToDelete] = useState<string[]>([]);
  const [selectionScope, setSelectionScope] = useState<SelectionScope | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<SelectedEntityIds>(EMPTY_SELECTED_ENTITY_IDS);
  const [searchParams] = useSearchParams();
  const { mapId } = useParams();
  const { data, loading, refetch } = useCompetencyMapQuery({
    variables: { competencyMapId: mapId! },
    skip: !mapId,
  });

  const { data: competencyMapGenQuestionsLogsData, refetch: refetchCompetencyMapGenQuestionsLog } =
    useCompetencyMapGenQuestionsLogQuery({ variables: { competencyMapId: mapId! } });

  const competencyMap = data?.competencyMap;
  const competencyMapFiles = competencyMap?.files || [];
  const hasCompetencyMapFiles = competencyMapFiles.length > 0;
  const competencyMapWithProgressInfo: CompetencyMapWithProgressInfo | undefined = useMemo(
    () => getCompetencyMapWithProgressInfo(competencyMap),
    [competencyMap]
  );

  const form = useForm<CompetencyMapInputs>({
    values: {
      id: competencyMap?.id,
      title: competencyMap?.title || "",
      comment: competencyMap?.comment,
      specialization: competencyMap?.specialization || "",
      grades: competencyMap?.grades?.join(", "),
      updatedAt: competencyMap?.updatedAt,
      createdAt: competencyMap?.createdAt,
      isRepositoryCheck: competencyMap?.isRepositoryCheck || false,
      questionsTime: secondsToMinutes(competencyMap?.questionsTime),
      testitngType: getTestitngTypeTitle(competencyMap?.questionsTime),
      knowledgeBlocks: competencyMap?.competences?.map((block) => ({
        id: block.id,
        title: block.title,
        knowledgeBlockCompetences: block.knowledgeBlockCompetences?.map((competence) => ({
          id: competence.knowledgeBlock.id,
          title: competence.knowledgeBlock.title,
          skills: competence.knowledgeBlock.skills,
        })),
      })),
    },
  });

  const disabled = competencyMap?.status === Competency_Map_Status.Generating;

  const { control } = form;
  const fieldArray = useFieldArray({
    control,
    name: "knowledgeBlocks",
    keyName: "formId",
  });

  const { append, replace } = fieldArray;
  const watchedKnowledgeBlocks = useWatch({ control, name: "knowledgeBlocks" });
  const selectionTree = useMemo(() => buildSelectionTree(watchedKnowledgeBlocks), [watchedKnowledgeBlocks]);
  const activeTabParam = useMemo(() => {
    const params = qs.parse(searchParams.toString()) as { tab?: string };
    return params.tab;
  }, [searchParams]);
  const activeTab = activeTabParam || Competency_Map_Tabs_Values.constructor;
  const selectionMode = Boolean(selectionScope);

  useEffect(() => {
    setSelectedEntities((prev) => normalizeSelectedEntityIds(prev, selectionTree));
  }, [selectionTree]);

  useEffect(() => {
    if (
      (selectionScope === "constructor" && activeTab !== Competency_Map_Tabs_Values.constructor) ||
      (selectionScope === "questions" && activeTab !== Competency_Map_Tabs_Values.questionsList)
    ) {
      setSelectionScope(null);
      setSelectedEntities(EMPTY_SELECTED_ENTITY_IDS);
    }
  }, [activeTab, selectionScope]);

  const handleAddBlock = () => {
    append({ title: "", knowledgeBlockCompetences: undefined });
  };

  const handleClearSelectionMode = () => {
    setSelectionScope(null);
    setSelectedEntities(EMPTY_SELECTED_ENTITY_IDS);
  };

  const handleToggleSelectionMode = (scope: SelectionScope) => {
    if (selectionScope === scope) {
      handleClearSelectionMode();
      return;
    }

    setSelectionScope(scope);
    setSelectedEntities(EMPTY_SELECTED_ENTITY_IDS);
  };

  const handleToggleSelectionEntity = (entity: SelectionEntity) => {
    setSelectedEntities((prev) => toggleSelectionEntity(prev, selectionTree, entity));
  };

  const title = `Карта компетенций\n${competencyMap?.title ? ` «${competencyMap.title}»` : ""}`;
  useSetTabTitle(title, competencyMap?.title);

  const TABS: TabLayoutProps["tabs"] = [
    {
      id: Competency_Map_Tabs_Values.constructor,
      title: "Структура КК",
      component: (
        <ConstructorTab
          fieldArray={fieldArray}
          form={form}
          isEditable={isEditable}
          heightFitContentSignal={isHeaderExpanded}
          selectionMode={selectionMode}
        />
      ),
      sx: { "& > div": { px: 0 } },
    },
    {
      id: Competency_Map_Tabs_Values.questionsList,
      title: "Список вопросов",
      component: (
        <QuestionsListTab
          heightFitContentSignal={isHeaderExpanded}
          competencyMap={competencyMap}
          selectionMode={selectionMode}
          refetchCompetencyMap={refetch}
        />
      ),
      disabled: isCreate,
      sx: { "& > div": { px: 0 } },
    },
    {
      id: Competency_Map_Tabs_Values.testingList,
      title: "Тестирования",
      component: <TestingListTab />,
      disabled: isCreate,
      sx: { "& > div": { px: 0 } },
    },
    ...(hasCompetencyMapFiles
      ? [
          {
            id: Competency_Map_Tabs_Values.documents,
            title: "Документы",
            component: <DocumentsTab files={competencyMapFiles} />,
            disabled: isCreate,
            sx: { "& > div": { px: 0 } },
          },
        ]
      : []),
  ];

  const handleRefetchCompetencyMapGenQuestionsLog: CompetencyMapGenQuestionsLogQueryResult["refetch"] = (input) => {
    return refetchCompetencyMapGenQuestionsLog(input).finally(() => setNavigationOpen(true));
  };

  if (loading) return <CircularLoading />;

  return (
    <>
      <ConstructorTabContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          skillsIdsToDelete,
          setSkillsIdsToDelete,
          refetchCompetencyMapGenQuestionsLog,
          onOpenGenerateQuestionsForCMModal: (selectParts) => setGenerateQuestionsForCMModalData({ selectParts }),
          selectionScope,
          selectionMode,
          selectedEntities,
          selectionTree,
          onToggleSelectionMode: handleToggleSelectionMode,
          onClearSelectionMode: handleClearSelectionMode,
          onToggleSelectionEntity: handleToggleSelectionEntity,
        }}>
        <Form form={form}>
          <Stack sx={{ mb: 1 }}>
            <Header
              competencyMapWithProgressInfo={competencyMapWithProgressInfo}
              disabled={disabled}
              isCreate={isCreate}
              isEditable={isEditable}
              setIsHeaderExpanded={setIsHeaderExpanded}
              setIsEditable={setIsEditable}
            />
          </Stack>
          <Box sx={{ position: "relative" }}>
            <ActionButtons
              competencyMap={competencyMap}
              isEditable={isEditable}
              setIsEditable={setIsEditable}
              disabled={disabled}
              navigationOpen={navigationOpen}
              setNavigationOpen={setNavigationOpen}
            />
            <Stack
              spacing={1}
              alignItems='flex-end'>
              <TabLayout
                tabs={TABS}
                defaultTab='constructor'
                sx={{
                  width: "100%",
                  "& .MuiTabPanel-root > div": {
                    backgroundColor: "transparent",
                  },
                }}
                withSearchParams
                renderNavigationComponent={(navigation, tab) => {
                  const isConstructorTab = tab === Competency_Map_Tabs_Values.constructor;
                  const isQuestionsTab = tab === Competency_Map_Tabs_Values.questionsList;
                  const showSelectionButton = isConstructorTab || isQuestionsTab;
                  const isSelectionButtonActive =
                    (isConstructorTab && selectionScope === "constructor") ||
                    (isQuestionsTab && selectionScope === "questions");

                  return (
                    <Stack
                      direction='row'
                      justifyContent='space-between'>
                      {navigation}
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-end", md: "center" }}
                        flexWrap='wrap'>
                        {isConstructorTab && (
                          <ButtonWithPermissions
                            buttonText='Добавить компетенцию'
                            sx={{
                              width: "100%",
                              backgroundColor: "white",
                              color: "text.primary",
                              "&:hover": { color: "white" },
                            }}
                            onClick={handleAddBlock}
                            disabled={!isEditable}
                          />
                        )}
                        {showSelectionButton && (
                          <Tooltip
                            title={
                              isConstructorTab ? structureSelectionModeTooltipText : questionsSelectionModeTooltipText
                            }>
                            <span>
                              <Button
                                variant='contained'
                                color='secondary'
                                onClick={() =>
                                  handleToggleSelectionMode(isConstructorTab ? "constructor" : "questions")
                                }
                                disabled={isCreate || isEditable || disabled}
                                sx={{
                                  width: "100%",
                                  backgroundColor: isSelectionButtonActive ? "primary.main" : "white",
                                  color: isSelectionButtonActive ? "common.white" : "text.primary",
                                  "&:hover": {
                                    backgroundColor: isSelectionButtonActive ? "primary.dark" : "white",
                                  },
                                  "&.Mui-disabled": {
                                    backgroundColor: "action.disabledBackground",
                                  },
                                }}>
                                Выбрать
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <MoreActionsButton
                          competencyMap={competencyMap}
                          isCreate={isCreate}
                          onOpenCopyModal={() => setCopyModalState({})}
                          onOpenGenerateQuestionsForCMModal={() =>
                            setGenerateQuestionsForCMModalData({ hideScopeAndPrompts: true })
                          }
                        />
                      </Stack>
                    </Stack>
                  );
                }}
                renderContent={(panels) => (
                  <Grid
                    container
                    spacing={1}
                    alignItems='flex-start'
                    sx={{ mt: 0 }}>
                    {navigationOpen && (
                      <Grid
                        item
                        xs={12}
                        lg={4}>
                        <ProgressInfo
                          title={title}
                          competencyMapWithProgressInfo={competencyMapWithProgressInfo}
                          competencyMapGenQuestionsLogsData={competencyMapGenQuestionsLogsData}
                          refetchCompetencyMapGenQuestionsLog={handleRefetchCompetencyMapGenQuestionsLog}
                          competencyMap={competencyMap}
                          refetchCompetencyMap={refetch}
                          heightFitContentSignal={isHeaderExpanded}
                        />
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={12}
                      lg={navigationOpen ? 8 : 12}>
                      {panels}
                    </Grid>
                  </Grid>
                )}
              />
            </Stack>
          </Box>
          <SelectionActions
            competencyMap={competencyMap}
            isCreate={isCreate}
            setIsEditable={setIsEditable}
            setSelectedEntities={setSelectedEntities}
            form={form}
            replaceKnowledgeBlocks={replace}
            selectedEntities={selectedEntities}
            selectionTree={selectionTree}
            selectionScope={selectionScope}
            onClearSelectionMode={handleClearSelectionMode}
            onOpenGenerateQuestions={(selectParts) => setGenerateQuestionsForCMModalData({ selectParts })}
            onOpenCopyMap={(selectParts) =>
              setCopyModalState({
                selectParts,
                titleText: "Создать карту компетенций",
                inputLabel: "Название новой карты компетенций",
                actionButtonText: "Создать",
              })
            }
          />
        </Form>
      </ConstructorTabContext.Provider>
      {copyModalState && competencyMap && (
        <CopyMapModal
          open={Boolean(copyModalState)}
          onClose={() => setCopyModalState(null)}
          onSuccess={handleClearSelectionMode}
          originalMap={{ id: competencyMap.id, title: competencyMap.title }}
          selectParts={copyModalState.selectParts}
          titleText={copyModalState.titleText}
          inputLabel={copyModalState.inputLabel}
          actionButtonText={copyModalState.actionButtonText}
        />
      )}
      {generateQuestionsForCMModalData && (
        <GenerateQuestionsForCMModal
          competencyMap={competencyMap}
          open={Boolean(generateQuestionsForCMModalData)}
          onClose={() => setGenerateQuestionsForCMModalData(null)}
          onSuccess={handleClearSelectionMode}
          refetchCompetencyMapGenQuestionsLog={handleRefetchCompetencyMapGenQuestionsLog}
          selectParts={generateQuestionsForCMModalData.selectParts}
          hideScopeAndPrompts={generateQuestionsForCMModalData.hideScopeAndPrompts}
        />
      )}
    </>
  );
};

export default CompetencyMapView;
